import { useApolloClient, useQuery } from "@apollo/client";
import CommentOutlined from "@mui/icons-material/CommentOutlined";
import {
  Badge,
  Box,
  Divider,
  GridLegacy as Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { IssueCount } from "components/IssueCount";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { SubmissionCommentsPanel } from "components/Submission/SubmissionCommentsPanel";
import { getStatusDisplay } from "components/Submission/SubmissionStatusChipWithPopover";
import { TruncateTypography } from "components/TruncateTypography";
import { isStepperEligibleSubmission } from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { CommentThreadFragment, SubmissionStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { stepperStepPath } from "hooks/useStepperNavigation";
import {
  useSubmissionIssuesGroupedByStep,
  useSubmissionStepStates,
} from "hooks/useSubmissionSteps";
import { ReactNode, useState } from "react";
import { Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { isSubmissionFrozen } from "util/isSubmissionFrozen";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { getSavedWasteSubmissionsUrl } from "../navigation";
import { GET_SUBMISSION_STEPPER_COMMENTS_QUERY, GET_SUBMISSION_STEPPER_QUERY } from "./queries";
import { SubmissionStepStatusIcon } from "./SubmissionStepStatusIcon";
import { WasteStepperProvider } from "./WasteStepperContext";

const FacilityInfo = ({
  loading,
  name,
  state,
}: {
  loading: boolean;
  name?: string | null;
  state?: string | null;
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Stack sx={{ px: 3, py: 2 }}>
        <Skeleton variant="text" height={theme.spacing(3)} />
        <Skeleton variant="text" />
      </Stack>
    );
  }

  return (
    <Stack sx={{ px: 3, py: 2 }}>
      <TruncateTypography lines={3} fontWeight="medium">
        {name}
      </TruncateTypography>
      {state && (
        <Typography variant="caption" color="text.secondary">
          {state}
        </Typography>
      )}
    </Stack>
  );
};

// Maps a submission status to a chip color, mirroring the legacy submissions
// list's per-status colors but constrained to StatusChip's semantic palette —
// the legacy purples (ENCAMP_INITIAL_REVIEW, PROCESSING_SUBMISSION) have no
// semantic role, so they approximate to info/warning. Unmapped statuses fall
// back to a neutral chip.
const STATUS_CHIP_COLOR: Partial<Record<SubmissionStatus, StatusChipColor>> = {
  [SubmissionStatus.NotStarted]: "warning",
  [SubmissionStatus.InProgress]: "info",
  [SubmissionStatus.EncampInitialReview]: "info",
  [SubmissionStatus.InReview]: "info",
  [SubmissionStatus.ProcessingSubmission]: "warning",
  [SubmissionStatus.FilingComplete]: "success",
  [SubmissionStatus.NotReporting]: "default",
};

/**
 * Submission type / method + status chip, shown under the facility (mirrors the
 * TierII report sidebar's year/type + status chip block).
 */
const SubmissionInfo = ({
  loading,
  submissionType,
  submissionMethod,
  status,
}: {
  loading: boolean;
  submissionType?: string | null;
  submissionMethod?: string | null;
  status?: SubmissionStatus | null;
}) => {
  const theme = useTheme();

  if (loading || !status) {
    return (
      <Box sx={{ px: 3, pt: 2, pb: 3 }}>
        <Skeleton variant="text" height={theme.spacing(4)} />
        <Skeleton variant="text" />
        <Skeleton variant="rounded" height={theme.spacing(4)} width={theme.spacing(14)} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3 }}>
      <Box sx={{ mb: 2 }}>
        {submissionType && (
          <Typography variant="h6">{prettyPrintEnumValue(submissionType)}</Typography>
        )}
        {submissionMethod && (
          <Typography variant="caption" color="text.secondary">
            {prettyPrintEnumValue(submissionMethod)}
          </Typography>
        )}
      </Box>
      <StatusChip
        variant="outlined"
        label={getStatusDisplay(status)}
        color={STATUS_CHIP_COLOR[status] ?? "default"}
      />
    </Box>
  );
};

const StepNavItem = ({
  text,
  icon,
  selected,
  issueCount,
  onClick,
}: {
  text: string;
  icon: ReactNode;
  selected: boolean;
  issueCount?: number;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick} selected={selected} sx={{ pl: 3 }}>
        <ListItemIcon sx={{ minWidth: 0, mr: theme.spacing(2) }}>{icon}</ListItemIcon>
        <ListItemText primary={text} sx={{ mr: theme.spacing(1) }} />
        <IssueCount issueCount={issueCount} />
      </ListItemButton>
    </ListItem>
  );
};

/**
 * Shell for the 8700-12 (RCRAInfo myRCRAid) submission stepper. Mirrors the
 * TierII report shell: a left sidebar (facility header + step nav), an
 * `<Outlet/>` for the active step, and a right-rail comments panel opened from
 * the comment icon at the content's top-right. Resolves the single facility
 * from the submission and provides shared state via `WasteStepperContext`.
 *
 * Non-8700-12 submissions are redirected to the read-only submission view.
 */
export function WasteSubmissionStepper() {
  const params = useParams<{
    tenantId: string;
    submissionId: string;
    "*": string;
  }>();
  const { tenantId = "", submissionId = "" } = params;
  // The active step's route slug. The stepper index (overview) has an empty
  // slug; step routes append their slug (e.g. `.../stepper/contacts`).
  const selected = params["*"] ?? "";
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, isStaff } = useCurrentUser();

  // Match the breadcrumb header of the read-only / create-edit submission views:
  // Waste / Submissions / Edit Submission. The stepper is only ever opened for an
  // existing submission, so the leaf crumb is always "Edit Submission".
  const submissionsUrl = getSavedWasteSubmissionsUrl(tenantId, user?.email);
  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions", to: submissionsUrl },
    { label: "Edit Submission" },
  ]);

  const { data, loading, error } = useQuery(GET_SUBMISSION_STEPPER_QUERY, {
    variables: { id: submissionId },
    skip: !submissionId,
    fetchPolicy: "cache-and-network",
  });

  if (error) {
    throw error;
  }

  // The submission-wide comment thread lives in the right-rail panel so it's
  // reachable from every step (rather than only the final Verify & Submit
  // step). Rendered from the cache; optimistic add/delete write back to the
  // same cached query.
  const apolloClient = useApolloClient();
  const { data: commentsData } = useQuery(GET_SUBMISSION_STEPPER_COMMENTS_QUERY, {
    variables: { id: submissionId },
    skip: !submissionId,
    fetchPolicy: "cache-and-network",
  });
  const comments = commentsData?.submission?.comments ?? [];

  const updateCachedComments = (
    updater: (prev: CommentThreadFragment[]) => CommentThreadFragment[]
  ) => {
    apolloClient.cache.updateQuery(
      {
        query: GET_SUBMISSION_STEPPER_COMMENTS_QUERY,
        variables: { id: submissionId },
      },
      (existing) =>
        existing?.submission
          ? {
              submission: {
                ...existing.submission,
                comments: updater(existing.submission.comments ?? []),
              },
            }
          : existing
    );
  };

  // Comments live in a right-rail panel opened from the comment icon button.
  // "Unread" is tracked client-side: the icon badge counts comments newer than
  // the last time this browser opened the panel, and those comments are
  // highlighted in the thread for that viewing (opening marks them read, so
  // the highlight is gone on the next visit — per the Figma annotation).
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Watermark to highlight against while the panel is open; undefined = no
  // highlighting (panel was opened without unread state), null = never viewed
  // before (highlight everything).
  const [highlightAfter, setHighlightAfter] = useState<string | null | undefined>(undefined);
  const commentsViewedKey = `wasteSubmissionCommentsViewedAt:${submissionId}`;
  const [commentsViewedAt, setCommentsViewedAt] = useState<string | null>(() =>
    localStorage.getItem(commentsViewedKey)
  );
  // Your own comments are read by definition — without this, adding a comment
  // while the panel is open would immediately badge (and later highlight) it.
  const unreadCommentCount = comments.filter(
    (c) =>
      !(user?.id && c.user?.id === user.id) &&
      (!commentsViewedAt || new Date(c.createdAt) > new Date(commentsViewedAt))
  ).length;

  const markCommentsViewed = () => {
    const now = new Date().toISOString();
    localStorage.setItem(commentsViewedKey, now);
    setCommentsViewedAt(now);
  };

  const toggleComments = () => {
    if (commentsOpen) {
      // Catch anything that arrived while the panel was open.
      markCommentsViewed();
      setCommentsOpen(false);
      return;
    }
    setHighlightAfter(commentsViewedAt);
    markCommentsViewed();
    setCommentsOpen(true);
  };

  const submission = data?.submission;
  const facilityId = submission?.facilitySubmissions?.[0]?.facility?.id ?? "";
  const facility = submission?.facilitySubmissions?.[0]?.facility;

  const stepStates = useSubmissionStepStates(
    submission?.stepperSteps ?? [],
    facilityId,
    submission?.status
  );
  const issuesByStep = useSubmissionIssuesGroupedByStep(facilityId);
  const isFrozen = isSubmissionFrozen(submission?.status);
  // Staff own the stepper's data entry, reviewer management, and filing.
  // Customers get a read-only render (and, if they're the active reviewer, an
  // approve/reject action surfaced separately). A frozen submission's data is
  // read-only for everyone, so editing also stops once frozen.
  const canEdit = isStaff && !isFrozen;

  const sideBarWidth = theme.spacing(32);
  // ~310px per the Figma comments panel ("slider", 309px at the 1440 breakpoint).
  const commentsPanelWidth = theme.spacing(38.75);
  const topNavHeight = theme.spacing(15.25);

  // Once loaded, only stepper-eligible submissions that were actually created
  // through the stepper (`usesStepper`) belong here. Everything else — including
  // an uploaded 8700-12 — falls back to the read-only detail view.
  if (
    submission &&
    (!isStepperEligibleSubmission(submission.submissionType, submission.submissionMethod) ||
      !submission.usesStepper)
  ) {
    return <Navigate to={`/o/${tenantId}/waste/submissions/${submissionId}`} replace />;
  }

  return (
    <WasteStepperProvider
      value={{
        submissionId,
        facilityId,
        // `submission` is undefined while loading; the shell renders skeletons in
        // that case and only the provider's consumers (mounted via Outlet) need a
        // resolved submission, which is guaranteed once content renders below.
        submission: submission ?? null,
        stepStates,
        isFrozen,
        canEdit,
      }}
    >
      {/*
       * `nowrap` + `minWidth: 0` (content item) keep all three columns on one
       * flex line. Grid containers wrap by default and a flex item's implicit
       * minimum is its min-content width — the Review step's reviewers DataGrid
       * sets inline pixel column widths measured pre-panel, so opening the
       * comments panel made the line overflow and wrap it to the bottom.
       */}
      <Grid container sx={{ height: `calc(100vh - ${topNavHeight})`, flexWrap: "nowrap" }}>
        <Grid
          item
          sx={{
            width: sideBarWidth,
            flexShrink: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <FacilityInfo
              loading={loading && !submission}
              name={facility?.name}
              state={facility?.state}
            />
            <Divider />
            <SubmissionInfo
              loading={loading && !submission}
              submissionType={submission?.submissionType}
              submissionMethod={submission?.submissionMethod}
              status={submission?.status}
            />
            <List sx={{ p: 0 }}>
              {(submission?.stepperSteps ?? []).map(({ step }) => {
                const presentation = WASTE_SUBMISSION_STEP_PRESENTATION[step];
                const isSelected = selected === presentation.routeSlug;
                return (
                  <StepNavItem
                    key={step}
                    text={presentation.label}
                    icon={<SubmissionStepStatusIcon status={stepStates[step]} />}
                    selected={isSelected}
                    issueCount={issuesByStep[step]}
                    onClick={() =>
                      navigate(stepperStepPath(tenantId, submissionId, presentation.routeSlug))
                    }
                  />
                );
              })}
            </List>
          </Stack>
        </Grid>
        <Grid
          item
          xs
          sx={{
            // Shrinkable below min-content so the fixed-width rails always fit
            // on the single flex line (see the container comment).
            minWidth: 0,
            borderLeft: `1px solid ${theme.palette.divider}`,
            width: `calc(100% - ${sideBarWidth})`,
            maxWidth: "1200px",
            display: "flex",
            gap: 1,
            p: 3,
          }}
        >
          {/*
           * The shell is fixed-height (the Grid container is sized to the
           * viewport), so the content scrolls inside this box rather than
           * page-scrolling — keeping the sidebar, comment icon, and comments
           * panel pinned. `minHeight: 0` lets the flex child shrink so the
           * overflow actually engages, and `pb: 3` keeps the step's footer
           * button off the bottom edge on tall steps (otherwise content spilled
           * past the column's padding and the button sat flush against the
           * page bottom).
           */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              // minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              pb: 1,
            }}
          >
            {!submission ? (
              <Box>
                <Skeleton variant="text" height={theme.spacing(4)} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Box>
            ) : (
              <Outlet />
            )}
          </Box>
          {/*
           * Comment icon in its own gutter column at the content's top-right
           * (the step headers' title row, per the Figma placement). A real flow
           * column — not an absolute overlay — so full-width step content like
           * the frozen-submission banner ends beside the icon instead of
           * rendering underneath it. The badge counts comments that arrived
           * since this browser last opened the panel.
           */}
          <Box sx={{ flexShrink: 0, alignSelf: "flex-start", mt: -1 }}>
            <Tooltip title={commentsOpen ? "Hide comments" : "View comments"}>
              <IconButton
                aria-label={commentsOpen ? "Hide comments" : "View comments"}
                onClick={toggleComments}
              >
                <Badge badgeContent={unreadCommentCount} color="secondary" max={999}>
                  <CommentOutlined />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
        {/*
         * Right-rail comments panel (Figma "slider"): pinned to the viewport's
         * right edge (`ml: auto` absorbs any slack the content column's
         * max-width leaves), full height, opened from the comment icon.
         */}
        {submission && commentsOpen && (
          <Grid
            item
            sx={{
              width: commentsPanelWidth,
              flexShrink: 0,
              height: "100%",
              ml: "auto",
              borderLeft: `1px solid ${theme.palette.divider}`,
            }}
          >
            <SubmissionCommentsPanel
              submissionId={submissionId}
              comments={comments}
              highlightAfter={highlightAfter}
              onClose={toggleComments}
              commentAdded={(comment) => updateCachedComments((prev) => [...prev, comment])}
              commentDeleted={(commentId) =>
                updateCachedComments((prev) => prev.filter((c) => c.id !== commentId))
              }
            />
          </Grid>
        )}
      </Grid>
    </WasteStepperProvider>
  );
}
